import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
    X, Download, Star, CheckCircle2, AlertCircle, FileText, Calendar, Video, BookOpen, 
    Send, User, Loader, ChevronDown, Briefcase, Printer, Mail, Phone, MapPin, Award, 
    CheckCircle, ExternalLink, GraduationCap, Sparkles, Building, ShieldCheck, Check, Copy
} from 'lucide-react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
// jspdf-autotable v5 no longer patches jsPDF on import — the bare side-effect
// import leaves doc.autoTable undefined. Call the exported function directly.
import autoTable from 'jspdf-autotable';
import api from '../../utils/api';

/**
 * One passport row: skill name, a level bar, and the verified level.
 * Shared by both passport groups so required and additional skills read
 * identically.
 */
const SkillLevelRow = ({ skill }) => (
    <div className="flex items-center gap-6">
        <div className="w-1/3 text-[13px] font-semibold text-slate-700 leading-tight flex items-center gap-2">
            <span className="truncate" title={skill.name}>{skill.name}</span>
            {skill.isMustHave && (
                <span className="shrink-0 text-[9px] font-bold tracking-widest bg-slate-800 text-white px-1.5 py-0.5 rounded uppercase">Must</span>
            )}
        </div>
        <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
                className={`h-full transition-all duration-500 ease-out ${skill.level > 0 ? 'bg-[#002147]' : 'bg-transparent'}`}
                style={{ width: `${(skill.level / 5) * 100}%` }}
            />
        </div>
        <div className="w-32 text-right shrink-0">
            {skill.level > 0 ? (
                <span className="text-[#002147] font-bold text-[13px] tracking-wide">
                    LEVEL {skill.level}
                    {skill.requiredLevel ? <span className="text-slate-400 font-medium"> / {skill.requiredLevel}</span> : null}
                </span>
            ) : (
                <span className="text-slate-400 font-medium text-[13px] tracking-wide">UNVERIFIED</span>
            )}
        </div>
    </div>
);

/**
 * `allowRoleSelection` — two flows set this. (1) Job fair check-in: a walk-in
 * student has not applied to anything, so the recruiter picks which role to
 * score them against. (2) The Applicants page's grouped rows: a student with
 * several applications shows as one row, and the dropdown switches between the
 * roles they applied for — there the page passes `onRoleChange` and swaps the
 * whole applicant to the sibling application, so status updates always hit the
 * right one. Everywhere else the role is fixed and shown as plain text.
 * `roleSelectionLabel` overrides the "Scoring against" caption (the grouped
 * flow says "Applied for", which is what is actually true there).
 */

// ─── Profile verification rows (experience / projects / courses) ────────────
const yearsBetween = (start, end) => {
    const a = start ? new Date(start) : null;
    const b = end ? new Date(end) : new Date();
    if (!a || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
    const d = (b - a) / (1000 * 60 * 60 * 24 * 365.25);
    return d > 0 ? d : 0;
};
const parseRequiredYears = (raw) => {
    if (raw == null) return null;
    const t = String(raw).trim().toLowerCase();
    if (!t) return null;
    if (/fresher|entry|no experience|any/.test(t)) return 0;
    const m = t.match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
};
// Local fallback (walk-in students with no server payload). Mirrors utils/profileMatch on the backend.
const localProfileMatch = (applicant, job) => {
    const list = Array.isArray(applicant?.workExperience) && applicant.workExperience.length
        ? applicant.workExperience
        : (Array.isArray(applicant?.student?.workExperience) ? applicant.student.workExperience : []);
    let years = 0, internships = 0, jobs = 0;
    list.forEach(w => {
        if (!w) return;
        if (String(w.experienceType || w.type || '').toLowerCase().includes('intern')) internships++; else jobs++;
        years += yearsBetween(w.startDate, w.currentlyWorking ? null : w.endDate);
    });
    years = Math.round(years * 10) / 10;
    const reqY = parseRequiredYears(job?.experience);
    const parts = [];
    if (jobs) parts.push(`${jobs} job${jobs > 1 ? 's' : ''}`);
    if (internships) parts.push(`${internships} internship${internships > 1 ? 's' : ''}`);
    const expCand = list.length ? `${years} yr${years === 1 ? '' : 's'} (${parts.join(', ')})` : 'None entered';
    const experience = (reqY == null || reqY === 0)
        ? { notRequired: true, isMet: true, isHard: true, requiredText: reqY === 0 ? 'Freshers welcome' : 'Not specified', candidateText: expCand }
        : { notRequired: false, isMet: years >= reqY, isHard: true, requiredText: `${reqY}+ yr${reqY === 1 ? '' : 's'}`, candidateText: expCand };

    const domains = Array.isArray(job?.projectDomains) ? job.projectDomains.filter(Boolean) : [];
    const projects = (Array.isArray(applicant?.projects) && applicant.projects.length ? applicant.projects : (applicant?.student?.projects || [])).filter(p => p && (p.title || p.description));
    const kw = (d) => String(d).toLowerCase().replace(/[()/&,-]/g, ' ').split(/\s+/).filter(w => w.length > 2 && !['and','the','of','in','for','with','development','engineering','management','design'].includes(w));
    const matching = domains.length ? projects.filter(p => domains.some(d => (p.domain && String(p.domain).toLowerCase() === String(d).toLowerCase()) || kw(d).some(k => `${p.domain || ''} ${p.title || ''} ${p.description || ''}`.toLowerCase().includes(k)))) : [];
    const projCand = projects.length ? `${projects.length} project${projects.length > 1 ? 's' : ''}${domains.length ? (matching.length ? ` · ${matching.length} in required domain${matching.length > 1 ? 's' : ''}` : ' · none in required domains') : ''}` : 'None entered';
    const projectsRow = !domains.length
        ? { notRequired: true, isMet: true, isHard: false, requiredText: 'Not specified', candidateText: projCand }
        : { notRequired: false, isMet: matching.length > 0, isHard: false, requiredText: `Project work in ${domains.join(', ')}`, candidateText: projCand };
    return { experience, projects: projectsRow, courses: null };
};

// Client-side courses rule (walk-in path): mirrors utils/profileMatch.matchCourses on the backend.
const localCoursesMatch = (job, completedEnrollments) => {
    const required = Array.isArray(job?.courseSkills) ? job.courseSkills.filter(c => c && (c.course || c.title)) : [];
    const done = Array.isArray(completedEnrollments) ? completedEnrollments : [];
    if (!required.length) {
        return { notRequired: true, isMet: true, isHard: true, requiredText: 'Not specified', candidateText: `${done.length} course${done.length === 1 ? '' : 's'} completed` };
    }
    const doneIds = new Set(done.map(e => String(e.course?._id || e.course)));
    const label = (c) => [c.courseNumber, c.title].filter(Boolean).join(' ');
    const completed = required.filter(c => doneIds.has(String(c.course))).map(label);
    const pending = required.filter(c => !doneIds.has(String(c.course))).map(label);
    return {
        notRequired: false, isMet: pending.length === 0, isHard: true,
        requiredText: required.map(label).join(', '),
        candidateText: `Completed ${completed.length} of ${required.length}${pending.length ? ` · pending: ${pending.join(', ')}` : ''}`
    };
};

const profileRows = (applicant, job, fetchedCourses) => {
    const serverPm = applicant?.matchData?.profileMatch || applicant?.profileMatch;
    const pm = serverPm || localProfileMatch(applicant, job);
    if (!pm.courses && Array.isArray(fetchedCourses)) pm.courses = localCoursesMatch(job, fetchedCourses);
    const rows = [];
    const push = (name, r, hard) => {
        if (!r) return;
        rows.push({
            name,
            required: r.requiredText || 'Not specified',
            candidate: r.candidateText || 'None entered',
            isMet: r.notRequired ? true : !!r.isMet,
            resultText: r.notRequired ? 'Not required' : (r.isMet ? 'Meets' : (hard ? 'Does not meet' : 'Partial')),
            isHardFilter: hard
        });
    };
    push('Work Experience', pm.experience, true);
    push('Projects', pm.projects, false);
    if (pm.courses) {
        push('Human Intelligence Courses', pm.courses, true);
    } else {
        // No server payload and the on-demand fetch is still running / not
        // possible — keep the row visible rather than dropping it.
        const req = Array.isArray(job?.courseSkills) && job.courseSkills.length
            ? job.courseSkills.map(c => [c.courseNumber, c.title].filter(Boolean).join(' ')).join(', ')
            : 'Not specified';
        rows.push({ name: 'Human Intelligence Courses', required: req, candidate: fetchedCourses === null ? 'Checking…' : 'Not available', isMet: true, resultText: fetchedCourses === null ? 'Checking…' : 'Unverified', isHardFilter: false });
    }
    return rows;
};

const ApplicantPackModal = ({ isOpen, onClose, applicant, onStatusUpdate, readOnly = false, availableJobs = [], allowRoleSelection = false, onRoleChange = null, roleSelectionLabel = '' }) => {
    const [activeTab, setActiveTab] = useState('score_sheet');
    const [isGeneratingKit, setIsGeneratingKit] = useState(false);
    
    // Combine all sources of available jobs for this candidate/fair
    const allAvailableJobs = useMemo(() => {
        const pool = [];
        if (Array.isArray(availableJobs) && availableJobs.length > 0) {
            pool.push(...availableJobs);
        }
        if (Array.isArray(applicant?.fairJobs) && applicant.fairJobs.length > 0) {
            applicant.fairJobs.forEach(j => {
                if (!pool.some(p => (p._id || p.id) === (j._id || j.id))) pool.push(j);
            });
        }
        if (Array.isArray(applicant?.availableJobs) && applicant.availableJobs.length > 0) {
            applicant.availableJobs.forEach(j => {
                if (!pool.some(p => (p._id || p.id) === (j._id || j.id))) pool.push(j);
            });
        }
        if (applicant?.jobDetails && !pool.some(p => (p._id || p.id) === (applicant.jobDetails._id || applicant.jobDetails.id))) {
            pool.push(applicant.jobDetails);
        }
        if (applicant?.job && !pool.some(p => (p._id || p.id) === (applicant.job._id || applicant.job.id))) {
            pool.push(applicant.job);
        }
        return pool;
    }, [availableJobs, applicant]);

    const [selectedRoleJobId, setSelectedRoleJobId] = useState(
        applicant?.jobDetails?._id || applicant?.job?._id || (allAvailableJobs[0]?._id) || ''
    );

    useEffect(() => {
        const defaultId = applicant?.jobDetails?._id || applicant?.job?._id || (allAvailableJobs[0]?._id) || '';
        setSelectedRoleJobId(defaultId);
    }, [applicant, allAvailableJobs]);

    const currentJob = useMemo(() => {
        if (selectedRoleJobId && allAvailableJobs.length > 0) {
            const found = allAvailableJobs.find(j => (j._id || j.id) === selectedRoleJobId);
            if (found) return found;
        }
        return applicant?.jobDetails || applicant?.job || allAvailableJobs[0] || {};
    }, [selectedRoleJobId, allAvailableJobs, applicant]);

    // Interactive scorecard state
    const [scores, setScores] = useState({
        'Culture Fit': 0,
        'Communication': 0,
        'Motivation': 0
    });
    const [hoveredScores, setHoveredScores] = useState({
        'Culture Fit': 0,
        'Communication': 0,
        'Motivation': 0
    });

    const [isScheduling, setIsScheduling] = useState(false);
    const [meetLink, setMeetLink] = useState(null);
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [offerDetails, setOfferDetails] = useState({ package: '', date: '', recruiterName: '' });

    const submitOffer = () => {
        if (!offerDetails.package || !offerDetails.date || !offerDetails.recruiterName?.trim()) {
            toast.error('Please enter package details, date, and your digital signature');
            return;
        }
        if (typeof onStatusUpdate === 'function') {
            onStatusUpdate('Offer', { 
                offeredPackage: offerDetails.package, 
                offerDate: offerDetails.date,
                recruiterName: offerDetails.recruiterName.trim()
            });
        }
        setShowOfferModal(false);
    };

    const handleStatusChange = (newStatus) => {
        if (newStatus === 'Offer' || newStatus === 'Offered') {
            setShowOfferModal(true);
        } else if (typeof onStatusUpdate === 'function') {
            onStatusUpdate(newStatus);
        }
    };

    React.useEffect(() => {
        if (applicant?.status === 'Interview Scheduled') {
            setMeetLink('https://meet.google.com/abc-defg-hij');
        } else {
            setMeetLink(null);
        }
    }, [applicant?.status]);

    const handleSchedule = () => {
        setIsScheduling(true);
        setTimeout(() => {
            setMeetLink('https://meet.google.com/abc-defg-hij');
            toast.success('Interview scheduled successfully!');
            setIsScheduling(false);
            handleStatusChange('Interview Scheduled');
        }, 1200);
    };

    const handleGenerateInterviewKit = async () => {
        try {
            setIsGeneratingKit(true);
            const { data } = await api.post('/placements/generate-interview-kit', {
                applicationId: applicant._id || applicant.applicationId
            });
            if (data?.success) {
                toast.success('Interview kit generated successfully!');
                if (applicant) applicant.autoGeneratedQuestions = data.data;
            } else {
                toast.error(data?.message || 'Failed to generate interview kit');
            }
        } catch (error) {
            toast.error('Failed to generate interview kit');
        } finally {
            setIsGeneratingKit(false);
        }
    };
    const { matchData = {} } = applicant || {};
    const { skillMatch = {}, academicMatch = {} } = matchData;

    const LEVEL_MAP = { beginner: 1, intermediate: 2, advanced: 3, expert: 4, master: 5 };
    const toLevel = (s) => {
        if (!s) return 0;
        if (s.verifiedLevel != null && !isNaN(Number(s.verifiedLevel)) && Number(s.verifiedLevel) > 0) {
            return Number(s.verifiedLevel);
        }
        if (s.level != null && !isNaN(Number(s.level)) && Number(s.level) > 0) {
            return Number(s.level);
        }
        if (s.level && LEVEL_MAP[String(s.level).toLowerCase()]) {
            return LEVEL_MAP[String(s.level).toLowerCase()];
        }
        return 1;
    };

    // Verified skills only — self-reported entries are never surfaced here.
    const verifiedSkills = React.useMemo(() => {
        // Pick the first list that actually HAS something. A plain `a || b` chain
        // is wrong here because an empty array is truthy — a candidate whose
        // placement profile carries no skills short-circuited the chain at `[]`
        // and scored 0%, even though their student record held verified skills.
        const raw = [
            applicant?.student?.verifiedSkills,
            applicant?.verifiedSkills,
            applicant?.student?.skills,
            applicant?.skills,
            applicant?.placementProfile?.skills,
            applicant?.student?.placementProfile?.skills
        ].find((list) => Array.isArray(list) && list.length > 0) || [];
        if (!Array.isArray(raw)) return [];
        return raw
            .map((s) => (typeof s === 'string'
                ? { name: s, level: 1 }
                : { name: s.skill_name || s.name || '', level: toLevel(s) }))
            .filter((s) => s.name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicant]);

    // The comparison is derived dynamically from currentJob
    const contributions = React.useMemo(() => {
        const job = currentJob || {};
        const required = (job.structuredSkills?.length && job.structuredSkills)
            || (job.eligibility?.structuredSkills?.length && job.eligibility.structuredSkills)
            || (job.eligibility?.skills || job.skills || []).map((s) => ({
                skill_name: typeof s === 'string' ? s : (s.skill_name || s.name),
                requiredLevel: 3,
                isEssential: true,
            }));

        if (!required.length) {
            const serverRows = skillMatch.breakdown?.skillContributions || [];
            if (serverRows.length > 0) return serverRows;
            return [];
        }

        return required.map((req) => {
            const reqName = String(req.skill_name || req.name || '').trim();
            const match = verifiedSkills.find((v) => {
                const a = v.name.toLowerCase();
                const b = reqName.toLowerCase();
                return a === b || a.includes(b) || b.includes(a);
            });
            const requiredLevel = req.requiredLevel || 1;
            const verifiedLevel = match ? match.level : 0;
            return {
                skillName: reqName,
                requiredLevel,
                verifiedLevel,
                isMustHave: !!req.isEssential,
                ratio: requiredLevel > 0 ? Math.min(verifiedLevel / requiredLevel, 1) : 0,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentJob, skillMatch, verifiedSkills]);

    // Score falls back to the derived rows when the server did not supply one.
    const derivedScore = contributions.length > 0
        ? Math.round((contributions.reduce((sum, c) => sum + c.ratio, 0) / contributions.length) * 100)
        : 0;
    const displayScore = skillMatch.breakdown?.skillContributions?.length ? (skillMatch.score || 0) : derivedScore;
    const displayBand = skillMatch.breakdown?.skillContributions?.length
        ? (skillMatch.band || 'Match')
        : (derivedScore >= 70 ? 'Strong' : derivedScore >= 40 ? 'Partial' : 'Not a Match');
    const mustHavesMet = contributions.filter((c) => c.isMustHave).every((c) => c.ratio >= 1);

    // Describe the actual result. This was previously a hardcoded sentence
    // claiming a strong match on every essential skill, which contradicted the
    // table whenever the candidate scored poorly.
    const matchSummary = React.useMemo(() => {
        if (contributions.length === 0) return '';
        const met = contributions.filter((c) => c.ratio >= 1);
        const missedMust = contributions.filter((c) => c.isMustHave && c.ratio < 1);
        const partial = contributions.filter((c) => c.ratio > 0 && c.ratio < 1);
        const absent = contributions.filter((c) => c.verifiedLevel === 0);
        const n = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

        const parts = [];
        parts.push(`Verified on ${n(met.length, 'skill')} of ${contributions.length} required.`);

        if (missedMust.length > 0) {
            parts.push(`${n(missedMust.length, 'essential skill')} not met: ${missedMust.map((c) => c.skillName).join(', ')}.`);
        } else if (contributions.some((c) => c.isMustHave)) {
            parts.push('All essential skills are met.');
        }

        if (partial.length > 0) {
            parts.push(`${n(partial.length, 'skill')} verified below the required level.`);
        }
        if (absent.length > 0) {
            parts.push(`${n(absent.length, 'skill')} has no verified evidence on record.`);
        }

        parts.push('Only verified levels are scored — self-reported skills are excluded.');
        return parts.join(' ');
    }, [contributions]);

    // Only two states remain: scored, or the role genuinely defines no skills.
    const isUnscored = contributions.length === 0;

    // Verified skills the role does not ask for. Shown separately so a candidate
    // with real skills does not read as "has nothing" just because none of them
    // happen to be on this role's requirement list.
    const otherVerifiedSkills = React.useMemo(() => {
        if (contributions.length === 0) return [];
        const required = contributions.map((c) => String(c.skillName || '').toLowerCase());
        return verifiedSkills.filter((v) => {
            const name = v.name.toLowerCase();
            return !required.some((r) => r === name || r.includes(name) || name.includes(r));
        });
    }, [contributions, verifiedSkills]);

    const [exporting, setExporting] = useState(false);

    /**
     * Export the pack as a PDF using jsPDF + autotable, matching the convention
     * already used by College/Reports. Built from the same derived values the
     * modal renders, so the document can never disagree with the screen.
     */
    const handleDownloadPdf = () => {
        try {
            setExporting(true);
            const doc = new jsPDF();
            const NAVY = [26, 56, 132];
            const line = (y) => { doc.setDrawColor(226, 232, 240); doc.line(14, y, 196, y); };

            // Header band
            doc.setFillColor(...NAVY);
            doc.rect(0, 0, 210, 40, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(17);
            doc.setFont('helvetica', 'bold');
            doc.text('Verified Candidate Application Pack', 14, 17);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`${applicant.fullName || 'Candidate'}  |  ${applicant.collegeName || ''}`, 14, 26);
            doc.text(`Role: ${currentJob?.jobTitle || applicant.jobTitle || '-'}    Status: ${applicant.status || 'Matched'}`, 14, 33);

            doc.setTextColor(30, 41, 59);
            let y = 50;

            // Candidate summary
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Candidate', 14, y); y += 2; line(y); y += 6;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            [
                ['Email', applicant.email || '-'],
                ['Mobile', applicant.mobile || '-'],
                ['Qualification', applicant.qualification || '-'],
                ['Branch', applicant.branch || '-'],
                ['CGPA', applicant.cgpa ? `${Number(parseFloat(String(applicant.cgpa).replace(/[^0-9.]/g, '') || 0)).toFixed(2)} CGPA${applicant.academicRecords && applicant.academicRecords.length > 0 ? ` (Upto Sem ${Math.max(...applicant.academicRecords.map(r => r.semester))})` : ''}` : '-'],
                ['Active backlogs', String(applicant.activeBacklog !== undefined && applicant.activeBacklog !== null ? (applicant.activeBacklog === 0 ? '0 (Cleared)' : applicant.activeBacklog) : '-')],
            ].forEach(([k, v]) => { doc.text(`${k}: ${v}`, 14, y); y += 5; });

            // Skills match
            y += 4;
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Skills Match', 14, y); y += 2; line(y); y += 6;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(
                isUnscored
                    ? 'Not scored - this role defines no skill requirements.'
                    : `Score ${displayScore}% (${displayBand}) | Must-haves met: ${mustHavesMet ? 'Yes' : 'No'}`,
                14, y
            );
            y += 6;

            if (!isUnscored) {
                autoTable(doc, {
                    head: [['Required Skill', 'Required', 'Verified', 'Result']],
                    body: contributions.map((s) => [
                        s.skillName + (s.isMustHave ? '  (MUST)' : ''),
                        `Level ${s.requiredLevel}`,
                        s.verifiedLevel > 0 ? `Level ${s.verifiedLevel}` : 'None',
                        s.ratio >= 1 ? 'Meets' : (s.ratio === 0 ? 'Major gap' : 'Minor gap'),
                    ]),
                    startY: y,
                    theme: 'grid',
                    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 9 },
                    styles: { fontSize: 8, cellPadding: 2 },
                });
                y = doc.lastAutoTable.finalY + 8;
            }

            // Other verified skills
            if (otherVerifiedSkills.length > 0) {
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.text('Also Verified (not required for this role)', 14, y); y += 2; line(y); y += 6;
                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                const text = otherVerifiedSkills.map((s) => `${s.name}${s.level > 0 ? ` (L${s.level})` : ''}`).join(', ');
                const splitText = doc.splitTextToSize(text, 182);
                doc.text(splitText, 14, y);
                y += splitText.length * 4.5 + 4;
            }

            // Academic match
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Academic & Basic Eligibility', 14, y); y += 2; line(y); y += 6;
            autoTable(doc, {
                head: [['Criterion', 'Requirement', 'Candidate Value', 'Result']],
                body: academicRows.map((r) => [r.criterion, r.requirement, r.candidateValue, r.result]),
                startY: y,
                theme: 'grid',
                headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 9 },
                styles: { fontSize: 8, cellPadding: 2 },
            });
            y = doc.lastAutoTable.finalY + 8;

            doc.save(`${(applicant.fullName || 'Candidate').replace(/\s+/g, '_')}_Application_Pack.pdf`);
            toast.success('Application Pack PDF generated successfully');
        } catch (err) {
            console.error('PDF export error:', err);
            toast.error('Failed to generate PDF');
        } finally {
            setExporting(false);
        }
    };

    // Full verified skills for the Skills Passport tab.
    const allVerifiedSkills = verifiedSkills;

    // Required skills mapped into the passport shape for the "Required for this role" group.
    const requiredPassportSkills = React.useMemo(
        () => contributions.map((c) => ({
            name: c.skillName,
            level: c.verifiedLevel || 0,
            isMustHave: c.isMustHave,
            requiredLevel: c.requiredLevel,
        })),
        [contributions]
    );

    // `otherVerifiedSkills` is declared once, above — a second copy here made the
    // file fail to parse (duplicate const in the same scope).

    // Academic Match Calculation (Replicating the strict matching from the backend/details page)
    // Completed courses fetched on demand when the server did not supply a
    // profileMatch (job-fair walk-in check-ins) — keeps the courses row present
    // everywhere the pack opens.
    const [fetchedCourses, setFetchedCourses] = useState(null);
    useEffect(() => {
        const hasServer = !!(applicant?.matchData?.profileMatch || applicant?.profileMatch);
        const studentId = applicant?.student?._id || applicant?.studentId || (applicant?.student && typeof applicant.student === 'string' ? applicant.student : null);
        setFetchedCourses(null);
        if (!isOpen || hasServer || !studentId) return;
        let alive = true;
        api.get(`/courses/completed/${studentId}`)
            .then(r => { if (alive) setFetchedCourses(r.data?.data || []); })
            .catch(() => { if (alive) setFetchedCourses([]); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, applicant?._id, applicant?.student?._id]);

    const academicRows = React.useMemo(() => {
        if (!applicant) return [];
        const eligibility = currentJob?.eligibility || applicant.jobDetails?.eligibility || {};

        // Callers pass whatever their page happens to hold, and a degree's
        // specialization is `Mixed` in the schema — it can arrive as an array of
        // branches. Coerce before any string work rather than throwing on
        // `.toLowerCase is not a function`.
        const normalizeString = (str) => {
            if (!str) return '';
            const text = Array.isArray(str) ? str.filter(Boolean).join(' ') : String(str);
            return text.toLowerCase().replace(/[^a-z0-9]/g, '');
        };

        const allowedDegrees = eligibility.allowedDegrees || [];
        // Display form: an array of branches should read "A, B", not "AB".
        const toText = (v) => Array.isArray(v) ? v.filter(Boolean).join(', ') : (v == null ? '' : String(v));

        const degreeVal = toText(applicant.qualification);
        const degreeFullVal = toText(applicant.degreeFullName);
        const degreeMatch = allowedDegrees.length === 0 || allowedDegrees.some(d => {
            const normD = normalizeString(d);
            const normCandVal = normalizeString(degreeVal);
            const normCandFullVal = normalizeString(degreeFullVal);
            return normCandVal.includes(normD) || normCandFullVal.includes(normD) || normD.includes(normCandVal);
        });

        const allowedBranches = eligibility.allowedBranches || [];
        const branchVal = toText(applicant.branch)
            || toText(applicant.academic?.specialisation)
            || toText(Array.isArray(applicant.registration?.higherEducation) && applicant.registration?.higherEducation[0]?.specialization)
            || toText(applicant.registration?.department);
        const branchMatch = allowedBranches.length === 0 || allowedBranches.some(b => {
            const normB = normalizeString(b);
            const normCandVal = normalizeString(branchVal);
            return normCandVal.includes(normB) || normB.includes(normCandVal);
        });

        const requiredCGPA = Number(eligibility.minCGPA) || 0;
        const candidateCGPARaw = applicant.cgpa || applicant.registration?.cgpa || 0;
        const candidateCGPA = candidateCGPARaw ? Number(parseFloat(String(candidateCGPARaw).replace(/[^0-9.]/g, '') || 0).toFixed(2)) : 0;
        const cgpaMatch = candidateCGPA >= requiredCGPA;

        const noBacklogRequired = eligibility.noBacklog || false;
        const activeBacklogsCount = applicant.activeBacklog !== undefined && applicant.activeBacklog !== null ? Number(applicant.activeBacklog) : 0;
        const hasBacklog = activeBacklogsCount > 0;
        const backlogMatch = !noBacklogRequired || !hasBacklog;

        // Helper to parse student academic score
        const parseScore = (val) => {
            if (val === undefined || val === null || val === '') return { value: 0, isCgpa: false };
            const str = String(val).trim();
            const num = parseFloat(str.replace(/[^0-9.]/g, ''));
            if (isNaN(num) || num <= 0) return { value: 0, isCgpa: false };
            const isCgpa = str.toLowerCase().includes('cgpa') || num <= 10;
            return { value: num, isCgpa };
        };

        const formatReq = (hasReq, pct, cgpa) => {
            if (!hasReq || (pct === 0 && cgpa === 0)) return 'No Min';
            if (pct > 0 && cgpa > 0) return `Min ${pct}% / ${cgpa} CGPA`;
            if (pct > 0) return `Min ${pct}%`;
            return `Min ${cgpa} CGPA`;
        };

        const formatCand = (scoreObj) => {
            if (!scoreObj || scoreObj.value === 0) return 'N/A';
            return scoreObj.isCgpa ? `${scoreObj.value} CGPA` : `${scoreObj.value}%`;
        };

        // 12th Marks Check
        const req12thPct = eligibility.min12thPercentage || 0;
        const req12thCgpa = eligibility.min12thCGPA || (req12thPct > 0 ? req12thPct / 10 : 0);
        const hasMin12th = eligibility.hasMin12th || req12thPct > 0 || req12thCgpa > 0;
        const raw12th = applicant.twelfthDetails?.percentage || applicant.registration?.twelfthDetails?.percentage || applicant.twelfthDetails?.cgpa || applicant.registration?.twelfthDetails?.cgpa;
        const cand12th = parseScore(raw12th);
        let twelfthMatch = !hasMin12th;
        if (hasMin12th && cand12th.value > 0) {
            if (cand12th.isCgpa) {
                twelfthMatch = (req12thCgpa > 0 && cand12th.value >= req12thCgpa) || (req12thPct > 0 && (cand12th.value * 10) >= req12thPct);
            } else {
                twelfthMatch = (req12thPct > 0 && cand12th.value >= req12thPct) || (req12thCgpa > 0 && (cand12th.value / 10) >= req12thCgpa);
            }
        }

        // 10th Marks Check
        const req10thPct = eligibility.min10thPercentage || 0;
        const req10thCgpa = eligibility.min10thCGPA || (req10thPct > 0 ? req10thPct / 10 : 0);
        const hasMin10th = eligibility.hasMin10th || req10thPct > 0 || req10thCgpa > 0;
        const raw10th = applicant.tenthDetails?.percentage || applicant.registration?.tenthDetails?.percentage || applicant.tenthDetails?.cgpa || applicant.registration?.tenthDetails?.cgpa;
        const cand10th = parseScore(raw10th);
        let tenthMatch = !hasMin10th;
        if (hasMin10th && cand10th.value > 0) {
            if (cand10th.isCgpa) {
                tenthMatch = (req10thCgpa > 0 && cand10th.value >= req10thCgpa) || (req10thPct > 0 && (cand10th.value * 10) >= req10thPct);
            } else {
                tenthMatch = (req10thPct > 0 && cand10th.value >= req10thPct) || (req10thCgpa > 0 && (cand10th.value / 10) >= req10thCgpa);
            }
        }

        // Keys must stay name / required / candidate / resultText / isMet /
        // isHardFilter — that is what the comparison table below reads.
        return [
            {
                name: 'Degree / Qualification',
                required: allowedDegrees.length > 0 ? allowedDegrees.join(', ') : 'Any Degree',
                candidate: degreeVal || 'N/A',
                isMet: degreeMatch,
                resultText: degreeMatch ? 'Meets' : 'Does not meet',
                isHardFilter: true
            },
            {
                name: 'Branch / Specialization',
                required: allowedBranches.length > 0 ? allowedBranches.join(', ') : 'Any Branch',
                candidate: branchVal || 'N/A',
                isMet: branchMatch,
                resultText: branchMatch ? 'Meets' : 'Does not meet',
                isHardFilter: true
            },
            {
                name: 'CGPA',
                required: requiredCGPA > 0 ? `Min ${requiredCGPA}` : 'No Min',
                candidate: candidateCGPA > 0 ? `${candidateCGPA.toFixed(2)} CGPA${applicant.academicRecords && applicant.academicRecords.length > 0 ? ` (Upto Sem ${Math.max(...applicant.academicRecords.map(r => r.semester))})` : ''}` : 'N/A',
                isMet: cgpaMatch,
                resultText: cgpaMatch ? 'Meets' : candidateCGPA > 0 ? `${(requiredCGPA - candidateCGPA).toFixed(1)} Below` : 'Missing / Gap',
                isHardFilter: true
            },
            {
                name: 'Active Backlogs',
                required: noBacklogRequired ? 'No active backlogs' : 'No restriction',
                candidate: activeBacklogsCount === 0 ? '0 (Cleared)' : `${activeBacklogsCount} backlog${activeBacklogsCount === 1 ? '' : 's'}`,
                isMet: backlogMatch,
                resultText: backlogMatch ? 'Meets' : `${activeBacklogsCount} backlog${activeBacklogsCount === 1 ? '' : 's'}`,
                isHardFilter: true
            },
            {
                name: '12th Standard Marks',
                required: formatReq(hasMin12th, req12thPct, req12thCgpa),
                candidate: formatCand(cand12th),
                isMet: twelfthMatch,
                resultText: twelfthMatch ? 'Meets' : cand12th.value > 0 ? 'Below Requirement' : 'Missing / Gap',
                isHardFilter: false
            },
            {
                name: '10th Standard Marks',
                required: formatReq(hasMin10th, req10thPct, req10thCgpa),
                candidate: formatCand(cand10th),
                isMet: tenthMatch,
                resultText: tenthMatch ? 'Meets' : cand10th.value > 0 ? 'Below Requirement' : 'Missing / Gap',
                isHardFilter: false
            },
            // ── Profile verification: experience / projects / completed courses ──
            // Computed server-side (utils/profileMatch) from the student's own
            // profile and their completed course enrollments, so every viewer
            // shows the same result. Walk-in check-ins (no application yet) fall
            // back to a local experience/projects check; the courses row needs
            // enrollment data, so it only renders when the server supplied it.
            ...profileRows(applicant, currentJob, fetchedCourses)
        ];
    }, [applicant, currentJob, fetchedCourses]);

    // Only hard-filter rows can fail the gate; soft rows (e.g. projects) inform but never block.
    const academicPassed = academicRows.filter(r => r.isHardFilter).every(r => r.isMet);

    // These ids are the switch keys for the content blocks further down
    // (activeTab === 'academic', 'passport', 'interview', …). They had been
    // renamed to academic_match / skills_passport / scorecard without the blocks
    // being renamed to match, so those three tabs rendered nothing at all.
    const tabs = [
        { id: 'score_sheet', label: '1. Skills Match' },
        { id: 'academic', label: '2. Academic Match' },
        { id: 'passport', label: '3. Skills Passport' },
        { id: 'reports', label: '4. Reports' },
        { id: 'cv', label: '5. CV (Unverified)' },
        { id: 'cover', label: '6. Cover Letter' },
        { id: 'interview', label: 'Interview Scorecard' }
    ];

    // Optional chaining matters: this runs before the `!applicant` guard below,
    // so the modal is rendered with a null applicant while closed.
    if (['Offer', 'In Progress', 'Accepted', 'Hired'].includes(applicant?.status)) {
        tabs.push({ id: 'offer', label: 'Signed Offer Letter' });
    }

    const getScoreBadge = (score) => {
        if (score >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        if (score >= 60) return 'bg-blue-50 text-blue-700 border-blue-200';
        if (score >= 40) return 'bg-amber-50 text-amber-700 border-amber-200';
        return 'bg-rose-50 text-rose-700 border-rose-200';
    };

    if (!isOpen || !applicant) return null;

    const modalContent = (
        <div 
            onClick={onClose}
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-md p-4 overflow-hidden animate-fadeIn"
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-50 w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700/30 animate-scaleUp"
            >
                {/* Header */}
                <div className="bg-[#002147] px-6 py-4 text-white flex-shrink-0 border-b border-white/10">
                    <div className="flex items-center justify-between gap-4">
                        {/* Identity: avatar, name, then the supporting detail on one
                            line. The old layout split this across two rows with a
                            separate boxed role panel, which read as three competing
                            headers stacked on top of each other. */}
                        <div className="flex items-center gap-3.5 min-w-0">
                            <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center overflow-hidden shrink-0">
                                {(applicant.profileImage || applicant.student?.profilePic || applicant.student?.profileImage) ? (
                                    <img
                                        src={applicant.profileImage || applicant.student?.profilePic || applicant.student?.profileImage}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-base font-semibold text-white">
                                        {(applicant.fullName || applicant.name || 'C').charAt(0).toUpperCase()}
                                    </span>
                                )}
                            </div>

                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold tracking-wider text-white/45 uppercase">
                                    Verified Candidate Application Pack
                                </p>
                                <h2 className="text-xl font-bold text-white tracking-tight leading-tight truncate">
                                    {applicant.fullName || applicant.name || applicant.student?.name || 'Candidate'}
                                </h2>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-white/60">
                                    {(applicant.rollNumber || applicant.student?.rollNumber) && (
                                        <>
                                            <span className="font-mono text-white/75">{applicant.rollNumber || applicant.student?.rollNumber}</span>
                                            <span className="text-white/25">•</span>
                                        </>
                                    )}
                                    <span className="truncate">
                                        {applicant.collegeName || applicant.college || applicant.student?.college?.name || 'Partner College'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={handleDownloadPdf}
                                disabled={exporting}
                                className="px-3.5 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all text-xs font-semibold flex items-center gap-2 border border-white/15 backdrop-blur-sm active:scale-95 disabled:opacity-50"
                            >
                                {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
                                <span>Download PDF</span>
                            </button>

                            {/* Move Pipeline Status Selector — one consistent glassy pill
                                that matches the header, with a single custom chevron.
                                `custom-arrow` opts out of the global select caret in
                                index.css (which would otherwise render a second arrow). */}
                            <div className="relative inline-flex items-center">
                                <select
                                    value={applicant.status || 'Matched'}
                                    disabled={readOnly}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    className={`custom-arrow text-xs font-semibold pl-3.5 pr-9 py-2 rounded-xl outline-none cursor-pointer transition-all bg-white/10 text-white border border-white/15 backdrop-blur-sm shadow-sm ${
                                        readOnly ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white/20'
                                    }`}
                                >
                                    <option value="Applied" className="bg-slate-900 text-white">Status: Applied</option>
                                    <option value="Matched" className="bg-slate-900 text-white">Status: Matched</option>
                                    <option value="Shortlisted" className="bg-slate-900 text-white">Status: Shortlisted</option>
                                    <option value="Interview Scheduled" className="bg-slate-900 text-white">Status: Interview Scheduled</option>
                                    <option value="Interviewed" className="bg-slate-900 text-white">Status: Interviewed</option>
                                    <option value="Offered" className="bg-slate-900 text-white">Status: Offered</option>
                                    <option value="Hired" className="bg-slate-900 text-white">Status: Hired</option>
                                    <option value="Declined" className="bg-slate-900 text-white">Status: Declined</option>
                                    <option value="Rejected" className="bg-slate-900 text-white">Status: Rejected</option>
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-white/80" />
                            </div>

                            <button 
                                onClick={onClose}
                                className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-xl transition-all ml-1"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Role: a single quiet line, not a boxed panel. It is what the
                        scores below are measured against, so it belongs here — but as
                        supporting context, not a second focal point. */}
                    <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2 text-xs">
                        <Briefcase size={13} className="text-white/40 shrink-0" />
                        <span className="text-white/45">
                            {allowRoleSelection ? (roleSelectionLabel || 'Scoring against') : 'Applied for'}
                        </span>

                        {allowRoleSelection && allAvailableJobs.length > 0 ? (
                            <div className="relative inline-flex items-center">
                                <select
                                    value={selectedRoleJobId}
                                    onChange={(e) => {
                                        setSelectedRoleJobId(e.target.value);
                                        if (onRoleChange) onRoleChange(e.target.value);
                                    }}
                                    className="custom-arrow bg-white/15 hover:bg-white/20 text-white font-semibold text-xs rounded-lg pl-2.5 pr-7 py-1 border border-white/25 outline-none cursor-pointer focus:ring-1.5 focus:ring-white/40 transition-all max-w-[260px] shadow-2xs"
                                >
                                    {allAvailableJobs.map((j) => (
                                        <option key={j._id || j.id} value={j._id || j.id} className="bg-slate-900 text-white py-1">
                                            {j.jobTitle || j.title || 'Role'}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={13} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/70" />
                            </div>
                        ) : (
                            <span className="font-semibold text-white">
                                {currentJob?.jobTitle || currentJob?.title || applicant.jobTitle || applicant.role || 'Job Fair Role'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Decline Reason Banner */}
                {applicant.status === 'Declined' && applicant.declineReason && (
                    <div className="bg-rose-50 border-b border-rose-200 px-6 py-4 flex items-start gap-3 flex-shrink-0">
                        <AlertCircle className="text-rose-600 mt-0.5 shrink-0" size={18} />
                        <div>
                            <h4 className="text-sm font-bold text-rose-900">Candidate Declined Offer</h4>
                            <p className="text-sm text-rose-700 mt-0.5"><span className="font-semibold">Reason provided:</span> {applicant.declineReason}</p>
                        </div>
                    </div>
                )}

                {/* Navigation Tabs */}
                <div className="flex border-b border-slate-200 bg-white px-6 overflow-x-auto flex-shrink-0 hide-scrollbar">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                                activeTab === tab.id 
                                    ? 'border-[#002147] text-[#002147]' 
                                    : 'border-transparent text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-8 bg-white" id="pdf-content-area">
                    
                    {/* SECTION 1: SCORE SHEET */}
                    {activeTab === 'score_sheet' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-[#002147] mb-1">Skills Match Score Sheet</h3>
                            <p className="text-sm text-slate-500 mb-8">
                                Every skill <strong className="font-semibold text-slate-600">this role requires</strong>, and what the candidate has verified against each.
                                Rows come from the job posting, not from the candidate — see the Skills Passport tab for their full profile.
                                Verified levels only; nothing self-reported is scored.
                            </p>
                            
                            {/* Hero Score Box */}
                            <div className="bg-[#f8fafc] border border-slate-200 rounded-xl p-6 flex items-center gap-8 mb-10">
                                <div className="text-center shrink-0">
                                    {isUnscored ? (
                                        <>
                                            <div className="text-4xl font-bold text-slate-400">—</div>
                                            <div className="text-slate-500 font-medium text-sm mt-1">Not scored</div>
                                        </>
                                    ) : (
                                        <>
                                            <div className={`text-5xl font-bold ${displayScore >= 70 ? 'text-emerald-600' : displayScore >= 40 ? 'text-amber-600' : 'text-rose-600'}`}>{displayScore}%</div>
                                            <div className={`font-medium text-sm mt-1 ${displayScore >= 70 ? 'text-emerald-700' : displayScore >= 40 ? 'text-amber-700' : 'text-rose-700'}`}>{displayBand}</div>
                                        </>
                                    )}
                                </div>
                                <div className="flex-1 border-l border-slate-200 pl-8 min-w-0">
                                    <div className="text-lg font-bold text-slate-800">{applicant.fullName} • {applicant.collegeName}</div>
                                    <div className="text-slate-500 text-sm mt-1 mb-4">
                                        Direction: <span className="font-semibold text-slate-700">{currentJob?.jobTitle || applicant.jobTitle || 'All Fair Roles'}</span>
                                    </div>
                                    {isUnscored ? (
                                        <div className="text-sm text-slate-600">
                                            This role has no skill requirements defined, so there is nothing to score against.
                                            The candidate's verified skills are listed below.
                                        </div>
                                    ) : (
                                        <div className="flex gap-8">
                                            <div>
                                                <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Must-haves met</div>
                                                <div className="font-bold text-slate-700">{mustHavesMet ? 'Yes' : 'No'}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Skills fully met</div>
                                                <div className="font-bold text-slate-700">
                                                    {contributions.filter(s => s.ratio >= 1).length} of {contributions.length}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isUnscored ? (
                                /* No requirements to compare against — show what the candidate
                                   actually has, rather than an empty comparison table. */
                                <div className="rounded-lg border border-slate-200 overflow-hidden">
                                    <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex items-center justify-between gap-3">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Candidate's Verified Skills</span>
                                        <span className="text-xs font-medium text-slate-400">{verifiedSkills.length} verified</span>
                                    </div>
                                    {verifiedSkills.length > 0 ? (
                                        <div className="p-5 flex flex-wrap gap-2">
                                            {verifiedSkills.map((s, idx) => (
                                                <span key={idx} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-800">
                                                    {s.name}
                                                    {s.level > 0 && (
                                                        <span className="rounded bg-[#1a3884]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#1a3884]">L{s.level}</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="px-6 py-8 text-center text-sm text-slate-500">
                                            This candidate has no verified skills on record yet.
                                        </div>
                                    )}
                                    <div className="border-t border-slate-100 bg-amber-50/60 px-6 py-3 text-[12px] text-amber-800">
                                        Add required skills to this job posting to get a scored comparison for every applicant.
                                    </div>
                                </div>
                            ) : (
                                /* Comparison Table */
                                <div className="overflow-hidden rounded-lg border border-slate-200">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">Required Skill</th>
                                                <th className="px-6 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">Required</th>
                                                <th className="px-6 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">Verified</th>
                                                <th className="px-6 py-3 font-semibold text-slate-600 uppercase text-xs tracking-wider">Result</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {contributions.map((skill, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-6 py-4 font-medium text-slate-800">
                                                        <div className="flex items-center gap-2">
                                                            {skill.skillName}
                                                            {skill.isMustHave && <span className="text-[10px] bg-slate-800 text-white px-1.5 py-0.5 rounded leading-none">MUST</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-600">Level {skill.requiredLevel}</td>
                                                    <td className="px-6 py-4 text-slate-800 font-medium">{skill.verifiedLevel > 0 ? `Level ${skill.verifiedLevel}` : 'None'}</td>
                                                    <td className={`px-6 py-4 font-medium ${skill.ratio >= 1 ? 'text-emerald-600' : (skill.isMustHave && skill.ratio < 0.6 ? 'text-red-600' : 'text-amber-600')}`}>
                                                        <div className="flex items-center gap-1">
                                                            {skill.ratio >= 1 ? (
                                                                <><CheckCircle2 size={14}/> Meets</>
                                                            ) : (
                                                                <span>{skill.ratio === 0 ? 'Major gap' : 'Minor gap'}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* The table above only lists what the ROLE requires, so a
                                candidate's other verified skills were invisible here and
                                looked like they had none. Surface them alongside. */}
                            {!isUnscored && otherVerifiedSkills.length > 0 && (
                                <div className="mt-6 rounded-lg border border-slate-200 overflow-hidden">
                                    <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex items-center justify-between gap-3">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                                            Also Verified — Not Required For This Role
                                        </span>
                                        <span className="text-xs font-medium text-slate-400">{otherVerifiedSkills.length}</span>
                                    </div>
                                    <div className="p-5 flex flex-wrap gap-2">
                                        {otherVerifiedSkills.map((s, idx) => (
                                            <span key={idx} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-medium text-slate-800">
                                                {s.name}
                                                {s.level > 0 && (
                                                    <span className="rounded bg-[#1a3884]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#1a3884]">L{s.level}</span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="border-t border-slate-100 px-6 py-3 text-[12px] text-slate-500">
                                        These are verified but sit outside this role's requirements, so they do not affect the score above.
                                    </div>
                                </div>
                            )}

                            {!isUnscored && (
                                <div className={`mt-8 p-4 border rounded-lg text-sm font-medium ${
                                    displayScore >= 70 ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                        : displayScore >= 40 ? 'bg-amber-50 border-amber-200 text-amber-800'
                                            : 'bg-rose-50 border-rose-200 text-rose-900'
                                }`}>
                                    <strong>What this means:</strong> {matchSummary}
                                </div>
                            )}
                        </div>
                    )}

                    {/* SECTION 2: ACADEMIC MATCH */}
                    {activeTab === 'academic' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-[#002147] mb-1">Academic Match Score Sheet</h3>
                            <p className="text-sm text-slate-500 mb-8">How this candidate's academic credentials and history compare to your job requirements.</p>

                            {/* Hero Score Box */}
                            <div className="bg-[#f8fafc] border border-slate-200 rounded-xl p-6 flex items-center gap-8 mb-10">
                                <div className="text-center">
                                    {/* Falls back to the locally computed result: `academicMatch`
                                        comes from the backend match payload and is `{}` until that
                                        loads, which made this gate read FAILED for everyone. */}
                                    <div className={`text-3xl font-bold ${(academicMatch?.isEligible ?? academicPassed) ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {(academicMatch?.isEligible ?? academicPassed) ? 'PASSED' : 'FAILED'}
                                    </div>
                                    <div className="text-slate-500 font-medium text-[11px] mt-1 uppercase tracking-wider">Hard Filters Gate</div>
                                </div>
                                <div className="flex-1 border-l border-slate-200 pl-8">
                                    <div className="text-lg font-bold text-slate-800">{applicant.fullName} • {applicant.collegeName}</div>
                                    <div className="text-slate-500 text-sm mt-1 mb-4">Direction: {applicant.jobTitle}</div>
                                    <div className="flex gap-8">
                                        <div>
                                            <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Total Criteria Met</div>
                                            <div className="font-bold text-slate-700">{academicRows.filter(r => r.isMet).length} of {academicRows.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Comparison Table */}
                            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm bg-white">
                                <table className="w-full text-left text-[13px]">
                                    <thead className="bg-slate-50/80 border-b border-slate-200">
                                        <tr>
                                            <th className="px-6 py-3 font-bold text-slate-400 uppercase text-[10px] tracking-[0.1em]">Eligibility Criteria</th>
                                            <th className="px-6 py-3 font-bold text-slate-400 uppercase text-[10px] tracking-[0.1em]">Required Benchmark</th>
                                            <th className="px-6 py-3 font-bold text-slate-400 uppercase text-[10px] tracking-[0.1em]">Candidate Value</th>
                                            <th className="px-6 py-3 font-bold text-slate-400 uppercase text-[10px] tracking-[0.1em] text-right">Result</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {academicRows.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-6 py-3">
                                                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                                                        {row.name}
                                                        {row.isHardFilter && <span className="text-[9px] font-bold tracking-widest bg-slate-800 text-white px-2 py-0.5 rounded uppercase">Must</span>}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 text-slate-500 font-medium leading-relaxed">{row.required}</td>
                                                <td className="px-6 py-3 text-slate-900 font-bold">{row.candidate}</td>
                                                <td className="px-6 py-3 text-right">
                                                    <div className={`inline-flex items-center justify-end gap-1.5 font-bold whitespace-nowrap ${row.isMet ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                        <span>{row.resultText}</span>
                                                        {row.isMet ? <CheckCircle2 size={16} strokeWidth={2.5}/> : <X size={16} strokeWidth={2.5}/>}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* SECTION 3: PASSPORT */}
                    {activeTab === 'passport' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-[#002147] mb-8">Verified Skills Passport</h3>
                            
                            <div className="space-y-8">
                                {/* Group 1 — every skill the JOB asks for, with the candidate's
                                    verified level against each (or UNVERIFIED). Always shown, so
                                    the role's requirements are visible whether or not the
                                    candidate happens to have any of them. */}
                                {requiredPassportSkills.length > 0 && (
                                    <div>
                                        <div className="flex items-baseline justify-between gap-3 mb-6 border-b border-slate-100 pb-2">
                                            <h4 className="text-xs font-bold uppercase tracking-widest text-[#002147]">
                                                Required For This Role
                                            </h4>
                                            <span className="text-[11px] font-semibold text-slate-400">
                                                {requiredPassportSkills.filter(s => s.level > 0).length} of {requiredPassportSkills.length} verified
                                            </span>
                                        </div>
                                        <div className="space-y-4">
                                            {requiredPassportSkills.map((skill, idx) => (
                                                <SkillLevelRow key={idx} skill={skill} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Group 2 — what the candidate has verified beyond the role's list. */}
                                {otherVerifiedSkills.length > 0 && (
                                    <div>
                                        <div className="flex items-baseline justify-between gap-3 mb-6 border-b border-slate-100 pb-2">
                                            <h4 className="text-xs font-bold uppercase tracking-widest text-[#002147]">
                                                {requiredPassportSkills.length > 0
                                                    ? 'Other Verified Skills'
                                                    : 'Technical Skills (Manual & AI-Augmented Levels)'}
                                            </h4>
                                            <span className="text-[11px] font-semibold text-slate-400">{otherVerifiedSkills.length}</span>
                                        </div>
                                        <div className="space-y-4">
                                            {otherVerifiedSkills.map((skill, idx) => (
                                                <SkillLevelRow key={idx} skill={skill} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {requiredPassportSkills.length === 0 && otherVerifiedSkills.length === 0 && (
                                    <div className="rounded-lg border border-dashed border-slate-300 px-6 py-8 text-center text-sm text-slate-500">
                                        This candidate has no verified skills on record, and this role defines no required skills.
                                    </div>
                                )}

                                <div>
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-[#002147] mb-4 mt-8 border-t border-slate-100 pt-6">Whole-Person (Verified)</h4>
                                    <div className="flex flex-wrap gap-2">
                                        <div className="flex items-center gap-1.5 bg-white border border-[#002147]/20 rounded-full py-1 px-3">
                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Structure</span>
                                            <span className="text-[#002147] font-bold text-xs">L4</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 bg-white border border-[#002147]/20 rounded-full py-1 px-3">
                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Own</span>
                                            <span className="text-[#002147] font-bold text-xs">L4</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 bg-white border border-[#002147]/20 rounded-full py-1 px-3">
                                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Connect</span>
                                            <span className="text-[#002147] font-bold text-xs">L3</span>
                                        </div>
                                        <div className="flex items-center bg-[#002147] text-white font-medium text-[11px] rounded-full py-1 px-3 shadow-sm">
                                            AIQ Practitioner
                                        </div>
                                        <div className="flex items-center bg-[#002147] text-white font-medium text-[11px] rounded-full py-1 px-3 shadow-sm">
                                            PIQ Practitioner
                                        </div>
                                        <div className="flex items-center bg-[#002147] text-white font-medium text-[11px] rounded-full py-1 px-3 shadow-sm">
                                            SQ Aware
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SECTION 3: REPORTS */}
                    {activeTab === 'reports' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h2 className="text-2xl font-bold text-[#002147] mb-2 mt-4">Assessment reports</h2>
                            <p className="text-sm text-slate-500 mb-8">Summaries of the five verified assessment areas behind the Passport. Full reports available on request.</p>

                            {applicant?.student?.assessmentResults?.length > 0 ? (
                                <div className="grid grid-cols-2 gap-4">
                                    {applicant.student.assessmentResults.map((result, idx) => (
                                        <div key={idx} className="border border-slate-200 rounded-lg p-5 bg-white shadow-sm flex flex-col justify-between">
                                            <div className="flex justify-between items-start mb-3">
                                                <h4 className="font-bold text-slate-800 text-sm">{result.assessmentName || 'Verified Assessment'}</h4>
                                                <span className="bg-[#002147]/5 text-[#002147] font-bold text-[10px] px-2 py-0.5 rounded uppercase tracking-wider border border-[#002147]/10">
                                                    {result.assessmentCode || 'VERIFIED'}
                                                </span>
                                            </div>
                                            <div className="text-slate-500 text-xs leading-relaxed space-y-1">
                                                <p>Completed on: {new Date(result.submittedAt || result.startedAt).toLocaleDateString()}</p>
                                                {result.totalQuestions > 0 && (
                                                    <p>Questions answered: {result.answeredQuestions} / {result.totalQuestions}</p>
                                                )}
                                                {result.scores && Object.keys(result.scores).length > 0 && (
                                                    <div className="mt-2 text-[10px] font-medium bg-slate-50 p-2 rounded border border-slate-100 text-slate-600">
                                                        Score traits verified.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div className="border border-slate-200 rounded-lg p-5 bg-white shadow-sm flex flex-col justify-between">
                                        <div className="flex justify-between items-start mb-3">
                                            <h4 className="font-bold text-slate-800 text-sm">Integrity & Liveness</h4>
                                            <span className="bg-teal-50 text-teal-700 font-bold text-[10px] px-2 py-0.5 rounded uppercase tracking-wider border border-teal-100">Verified</span>
                                        </div>
                                        <p className="text-slate-500 text-xs leading-relaxed">
                                            Identity & liveness checked; unique test instances; no anomalies flagged.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg border border-slate-200">
                                    No completed assessments found for this candidate.
                                </div>
                            )}
                        </div>
                    )}

                    {/* SECTION 4: UNVERIFIED CV */}
                    {activeTab === 'cv' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-[#002147] mb-4">Curriculum Vitae</h3>
                            
                            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4 mb-8 flex gap-3 text-slate-600">
                                <AlertCircle className="shrink-0 text-slate-400 mt-0.5" size={18} />
                                <div className="text-sm">
                                    <strong className="text-slate-700">Candidate-provided — self-reported.</strong> Included for context. Not verified by SMAART and not part of the match score — only the verified Passport is scored.
                                </div>
                            </div>

                            {applicant.resumeUrl ? (
                                <iframe src={applicant.resumeUrl} className="w-full h-[600px] border border-slate-200 rounded-lg bg-slate-50" title="CV" />
                            ) : (
                                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg border border-slate-200">
                                    No CV uploaded
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'cover' && (
                        <div className="animate-fade-in max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-[#002147] mb-4">Cover Letter</h3>

                            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4 mb-8 flex gap-3 text-slate-600">
                                <AlertCircle className="shrink-0 text-slate-400 mt-0.5" size={18} />
                                <div className="text-sm">
                                    <strong className="text-slate-700">Written by the candidate for this job.</strong> Included for context. Not part of the match score.
                                </div>
                            </div>

                            {(applicant?.coverLetter || applicant?.application?.coverLetter) ? (
                                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{applicant?.coverLetter || applicant?.application?.coverLetter}</p>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg border border-slate-200">
                                    No cover letter submitted
                                </div>
                            )}
                        </div>
                    )}

                    {/* SECTION 6: INTERVIEW SCORECARD & SCHEDULER */}
                    {activeTab === 'interview' && (
                        <div className="animate-fade-in max-w-4xl mx-auto grid grid-cols-2 gap-8">
                            
                            {/* Left Col: Scheduler & AI Kit */}
                            <div className="space-y-6">
                                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                                    <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-4"><Calendar size={18} className="text-[#4f7dff]"/> Schedule Interview</h4>
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <input type="date" className="w-1/2 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:bg-white focus:ring-2 focus:ring-[#002147]/20 transition-colors text-slate-700" />
                                            <select defaultValue="" className="w-1/2 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 outline-none focus:bg-white focus:ring-2 focus:ring-[#002147]/20 transition-colors cursor-pointer text-slate-700">
                                                <option value="" disabled>Select time</option>
                                                {['09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM', '06:00 PM'].map(time => (
                                                    <option key={time} value={time}>{time}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        {!meetLink ? (
                                            <button 
                                                onClick={handleSchedule} 
                                                disabled={isScheduling} 
                                                className={`w-full text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${isScheduling ? 'bg-slate-400 cursor-not-allowed' : 'bg-[#002147] hover:bg-[#1a3884]'}`}
                                            >
                                                <Video size={16} /> {isScheduling ? 'Generating Link...' : 'Generate Meet Link & Schedule'}
                                            </button>
                                        ) : (
                                            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-center animate-fade-in">
                                                <p className="text-emerald-700 font-bold text-xs uppercase tracking-wider mb-1 flex items-center justify-center gap-1"><CheckCircle2 size={14} /> Interview Scheduled!</p>
                                                <a href={meetLink} target="_blank" rel="noopener noreferrer" className="text-[#002147] font-medium text-sm hover:underline">{meetLink}</a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="font-bold text-slate-800 flex items-center gap-2"><BookOpen size={18} className="text-purple-500"/> AI Interview Kit</h4>
                                        <button 
                                            onClick={handleGenerateInterviewKit}
                                            disabled={isGeneratingKit}
                                            className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium hover:bg-purple-200"
                                        >
                                            {isGeneratingKit ? 'Generating...' : 'Generate Questions'}
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500 mb-4">Questions tailored to probe collaboration, culture fit, and missing nice-to-haves based on their Passport.</p>
                                    
                                    {applicant.autoGeneratedQuestions?.length > 0 ? (
                                        <ul className="space-y-3">
                                            {applicant.autoGeneratedQuestions.map((q, i) => (
                                                <li key={i} className="bg-white p-3 rounded border border-slate-200 text-sm text-slate-700 shadow-sm leading-relaxed">{q}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-center py-6 text-slate-400 text-sm border border-dashed border-slate-300 rounded">
                                            Click generate to create targeted questions.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right Col: Scorecard */}
                            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col h-full">
                                <h4 className="font-bold text-slate-800 mb-1">Structured Scorecard</h4>
                                <p className="text-xs text-slate-500 mb-6">Technical skills are verified. Rate the candidate on human factors.</p>

                                <div className="space-y-5 flex-1">
                                    {['Culture Fit', 'Communication', 'Motivation'].map(cat => (
                                        <div key={cat}>
                                            <div className="text-sm font-medium text-slate-700 mb-2">{cat}</div>
                                            <div className="flex gap-2">
                                                {[1,2,3,4,5].map(star => {
                                                    const currentScore = scores[cat];
                                                    const hoverScore = hoveredScores[cat];
                                                    const isActive = star <= (hoverScore || currentScore);
                                                    return (
                                                        <Star 
                                                            key={star} 
                                                            size={24} 
                                                            onClick={() => setScores(prev => ({...prev, [cat]: star}))}
                                                            onMouseEnter={() => setHoveredScores(prev => ({...prev, [cat]: star}))}
                                                            onMouseLeave={() => setHoveredScores(prev => ({...prev, [cat]: 0}))}
                                                            className={`cursor-pointer transition-all duration-200 ${
                                                                isActive 
                                                                    ? 'text-amber-400 fill-amber-400' 
                                                                    : 'text-slate-200 hover:text-amber-400 hover:fill-amber-400'
                                                            }`} 
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <div className="pt-4">
                                        <div className="text-sm font-medium text-slate-700 mb-2">Interview Notes</div>
                                        <textarea 
                                            className="w-full border border-slate-200 rounded-lg p-3 text-sm h-32 bg-slate-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-[#4f7dff]/50" 
                                            placeholder="Leave objective feedback here..."
                                        ></textarea>
                                    </div>
                                </div>

                                <button className="w-full mt-6 bg-[#002147] text-white py-2.5 rounded-lg text-sm font-bold hover:bg-[#1a3884] transition-colors">
                                    Save Scorecard & Move to Offer
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'offer' && (
                        <div className="flex justify-center p-6 bg-slate-50 min-h-[500px]">
                            <div className="bg-white w-full max-w-3xl rounded-xl shadow-md p-10 border border-slate-200">
                                <style>{`
                                    @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
                                    .signature-font {
                                        font-family: 'Great Vibes', cursive;
                                        font-weight: 400;
                                    }
                                `}</style>
                                <div className="flex justify-between items-start border-b-2 border-slate-200 pb-6 mb-8">
                                    <div className="flex items-center gap-4">
                                        {(applicant.job?.companyLogo || applicant.job?.logo || applicant.jobDetails?.company?.logo) && (
                                            <img src={applicant.job?.companyLogo || applicant.job?.logo || applicant.jobDetails?.company?.logo} alt="Company Logo" className="w-16 h-16 object-contain rounded-lg border border-slate-100 p-1" />
                                        )}
                                        <div>
                                            <h2 className="text-2xl font-bold tracking-tight text-slate-900 uppercase">
                                                {applicant.jobTitle || 'Role'}
                                            </h2>
                                            <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest mt-1">Official Offer Letter</p>
                                        </div>
                                    </div>
                                    <div className="text-right text-sm text-slate-500 font-medium">
                                        <p>Date: <span className="text-slate-800 font-bold">{applicant.offerDate || 'N/A'}</span></p>
                                        <p>Ref: <span className="text-slate-800 font-bold">OFF/{applicant._id ? applicant._id.slice(-6).toUpperCase() : 'N/A'}/26</span></p>
                                    </div>
                                </div>
                                <div className="space-y-4 text-sm text-slate-700 leading-relaxed font-sans">
                                    <p>Dear <span className="font-bold">{applicant.fullName}</span>,</p>
                                    <p>We are thrilled to extend this formal offer of employment to you for the position of <span className="font-bold text-slate-900">{applicant.jobTitle || 'the offered role'}</span>.</p>
                                    <p>Based on your outstanding performance during our rigorous selection process and the verification of your skills through the SMAART platform, we are confident you will be a tremendous addition to our team.</p>
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 my-6 space-y-3 shadow-inner">
                                        <div className="flex justify-between border-b border-slate-200 pb-2">
                                            <span className="font-medium text-slate-500">Position</span>
                                            <span className="font-bold text-slate-900">{applicant.jobTitle || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between border-b border-slate-200 pb-2">
                                            <span className="font-medium text-slate-500">Total Compensation</span>
                                            <span className="font-bold text-green-700">{applicant.offeredPackage || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium text-slate-500">Status</span>
                                            <span className="font-bold text-[#002147] uppercase tracking-wide">{applicant.status}</span>
                                        </div>
                                    </div>
                                    <p>By signing below, you formally accept this offer and agree to the general terms of employment. Please retain a copy of this digital agreement for your records.</p>
                                </div>
                                <div className="mt-12 flex justify-between items-end border-t border-slate-100 pt-8">
                                    <div>
                                        <p className="text-slate-400 text-xs font-semibold tracking-wider uppercase mb-2">Authorized By</p>
                                        <div className="text-3xl signature-font text-black transform -rotate-2">
                                            {applicant.recruiterSignature || applicant.job?.recruiterName || 'Authorized Signatory'}
                                        </div>
                                        <div className="mt-3 border-t-2 border-slate-300 w-48 pt-1">
                                            <p className="text-xs font-bold text-slate-800">Company Representative</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-slate-400 text-xs font-semibold tracking-wider uppercase mb-2 text-left">Accepted By</p>
                                        <div className={`text-3xl signature-font transform -rotate-2 ${['Accepted', 'Hired'].includes(applicant.status) ? 'text-black' : 'text-slate-300'}`}>
                                            {['Accepted', 'Hired'].includes(applicant.status) ? (applicant.eSignature || applicant.fullName) : 'Awaiting Signature...'}
                                        </div>
                                        <div className="mt-3 border-t-2 border-slate-300 w-48 pt-1 text-left">
                                            <p className="text-xs font-bold text-slate-800">Candidate Digital Signature</p>
                                            {['Accepted', 'Hired'].includes(applicant.status) && (
                                                <p className="text-[10px] text-green-600 font-semibold flex items-center gap-1 mt-1">
                                                    ✓ Verified via SMAART
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                </div>
            </div>

            {/* Offer Details Modal */}
            {showOfferModal && (
                <div
                    // Covers the full viewport (the pack modal portals to body),
                    // so no sidebar offset — center on the screen itself.
                    // stopPropagation is load-bearing: without it any click in this
                    // dialog bubbles to the pack-modal overlay above, whose
                    // onClick={onClose} closes everything mid-typing.
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowOfferModal(false);
                    }}
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-slate-100 transform transition-all"
                    >
                        <style>{`
                            @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
                            .signature-font {
                                font-family: 'Great Vibes', cursive;
                                font-weight: 400;
                            }
                        `}</style>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2.5 bg-blue-50 text-[#002147] rounded-xl">
                                <Send size={20} strokeWidth={2} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 tracking-tight">Send Offer Letter</h3>
                                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Applicant: {applicant.fullName}</p>
                            </div>
                        </div>
                        
                        <p className="text-xs text-slate-500 mb-5 leading-relaxed">
                            Enter the final offer details below. The candidate will be notified instantly via their dashboard to review and digitally sign the agreement.
                        </p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">Offered Package</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                                    <input 
                                        type="text" 
                                        className="w-full pl-8 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#002147]/20 focus:border-[#002147] outline-none transition-all"
                                        value={offerDetails.package}
                                        onChange={(e) => setOfferDetails({...offerDetails, package: e.target.value})}
                                        placeholder="e.g. 12 LPA"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">Valid Until Date</label>
                                <input 
                                    type="date" 
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-2 focus:ring-[#002147]/20 focus:border-[#002147] outline-none transition-all"
                                    value={offerDetails.date}
                                    onChange={(e) => setOfferDetails({...offerDetails, date: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">Digital Signature</label>
                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                                    <input 
                                        type="text" 
                                        placeholder="Type your full name to e-sign"
                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#002147]/20 focus:border-[#002147] outline-none transition-all"
                                        value={offerDetails.recruiterName}
                                        onChange={(e) => setOfferDetails({...offerDetails, recruiterName: e.target.value})}
                                    />
                                    <div className="flex justify-between items-end px-2 pt-0.5">
                                        <div className="text-[10px] text-slate-500 font-medium pb-1">Signature Preview:</div>
                                        {offerDetails.recruiterName ? (
                                            <div className="signature-font text-2xl text-black tracking-wide" style={{ transform: 'rotate(-2deg)' }}>
                                                {offerDetails.recruiterName}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-slate-400 italic pb-1">Waiting for signature...</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-end gap-3 mt-6">
                            <button 
                                onClick={() => setShowOfferModal(false)}
                                className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={submitOffer}
                                className="px-6 py-2.5 bg-[#002147] text-white text-sm font-bold rounded-xl hover:bg-[#001530] shadow-md shadow-[#002147]/20 transition-all flex items-center gap-2"
                            >
                                <CheckCircle2 size={18} /> Confirm & Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

export default ApplicantPackModal;
